import { useState } from 'react'
import { toast } from 'react-hot-toast'
import { Loader2, MapPin, X } from 'lucide-react'
import { useQueryClient } from 'react-query'
import { apiService } from '@/services/apiService'
import { useAuthStore } from '@/stores/authStore'
import { checkAndRefreshPresence, useAutoAtLake } from '@/hooks/useAutoAtLake'

/**
 * One-time opt-in for geolocation-based "At The Lake" auto-detect, plus the
 * background check itself (useAutoAtLake) for members who already said yes.
 *
 * Shows only while User.autoInResidence is null/undefined — i.e. the member
 * has never answered. Either answer is saved server-side, so the prompt
 * appears once per member, not once per device. It also waits for the
 * first-run tutorial to finish so new members aren't greeted by two
 * overlapping cards.
 */
const AtLakeAutoPrompt = () => {
  const user = useAuthStore((s) => s.user)
  const updateUser = useAuthStore((s) => s.updateUser)
  const qc = useQueryClient()
  const [saving, setSaving] = useState<'yes' | 'no' | null>(null)
  const [dismissed, setDismissed] = useState(false)

  // Background auto-detect for members already opted in.
  useAutoAtLake()

  const neverAsked = user != null && user.autoInResidence == null
  const tutorialDone = user?.tutorialCompletedAt != null
  if (!neverAsked || !tutorialDone || dismissed) return null

  const answer = async (enabled: boolean) => {
    setSaving(enabled ? 'yes' : 'no')
    try {
      const updated = await apiService.setAutoAtLake(enabled)
      updateUser({ autoInResidence: updated.autoInResidence ?? enabled })

      if (!enabled) {
        setDismissed(true)
        return
      }

      // They opted in — locate them right now. This first call is also what
      // triggers the browser's location-permission prompt.
      const result = await checkAndRefreshPresence()
      setDismissed(true)
      if (result.status === 'refreshed') {
        updateUser({ inResidenceUntil: result.user.inResidenceUntil ?? null })
        qc.invalidateQueries({ queryKey: ['directory'] })
        toast.success(
          'You’re at Shorewood — marked as at the lake. This now happens automatically.'
        )
      } else if (result.status === 'denied') {
        // Without the browser permission the feature can't work; revert so
        // the member isn't left believing auto-detect is on.
        const reverted = await apiService.setAutoAtLake(false)
        updateUser({ autoInResidence: reverted.autoInResidence ?? false })
        toast.error(
          'Location permission was blocked, so auto-detect stayed off. You can re-enable it anytime from your profile.'
        )
      } else {
        toast.success(
          'Auto-detect is on. Next time you open the portal at Shorewood, you’ll be marked at the lake.'
        )
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error?.message || 'Could not save your preference'
      )
    } finally {
      setSaving(null)
    }
  }

  return (
    <div
      role="dialog"
      aria-label="At The Lake auto-detect"
      className="fixed z-40 bottom-24 lg:bottom-6 right-4 left-4 sm:left-auto sm:right-6 sm:max-w-sm rounded-3xl border border-mist/50 bg-paper shadow-lift p-5 animate-rise"
    >
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Not now"
        className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center text-slate hover:bg-mist/40 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="w-11 h-11 rounded-full bg-meadow/10 text-meadow flex items-center justify-center shrink-0">
          <MapPin className="w-5 h-5" />
        </div>
        <div>
          <h4 className="font-serif text-base font-semibold text-deepwater">
            Turn on At The Lake automatically?
          </h4>
          <p className="text-sm text-slate mt-1 text-pretty">
            When you open the portal at Shorewood, we can mark you as at the
            lake for neighbors — no toggle needed. Uses your device’s location
            only while the portal is open, and it fades out on its own after
            you leave.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-4 pl-14">
        <button
          type="button"
          onClick={() => answer(true)}
          disabled={saving !== null}
          className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-medium bg-meadow text-white shadow-paper hover:bg-meadow/90 transition-all disabled:opacity-60"
        >
          {saving === 'yes' && <Loader2 className="w-4 h-4 animate-spin" />}
          Yes, auto-detect
        </button>
        <button
          type="button"
          onClick={() => answer(false)}
          disabled={saving !== null}
          className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-medium border border-mist text-deepwater hover:border-primary hover:text-primary transition-all disabled:opacity-60"
        >
          {saving === 'no' && <Loader2 className="w-4 h-4 animate-spin" />}
          No thanks
        </button>
      </div>
    </div>
  )
}

export default AtLakeAutoPrompt
